import { useEffect, useRef } from "react";
import { BukMountainlocation } from "@component/kakaoMap/BukMountainLocation";
import { useNavigate } from "react-router-dom";

interface CustomMapProps {
  isInitLocationFlag: boolean;
  mapPath?: any[];
  markerList: {
    latitude: number;
    hardness: number;
    ntnlPntNo: string;
    tooltipTextList?: { type: string; tooltipText: string }[];
    markType: string[];
    isFocus?: boolean;
    sggValue: string;
    dataReceived?: boolean;
  }[];
  mapWidth: string;
  mapHeight: string;
  //드래그 및 줌인 줌 아웃 제한항 영역 지정 (좌하,우상)
  initMaxLocation?: {
    leftLatitude: number;
    leftHardness: number;
    rightLatitude: number;
    rightHardness: number;
  };
  maxLevel: number;
  currentLevel: number;
  initLocation: { latitude: number; hardness: number };
}

const KakaoMap = (props: CustomMapProps) => {
  const mapContainerRef = useRef(null);
  const navigate = useNavigate();

  const redImage = require("../../assets/images/mark_red.png");
  const blackImage = require("../../assets/images/mark_black.png");
  const grayImage = require("../../assets/images/mark_gray.png");
  //SDK 미로드(도메인 미등록, 네트워크 차단 등) 시 지도 영역에 안내만 표시하고 나머지 화면은 정상 렌더링 되도록 처리
  const showMapUnavailable = (container: HTMLElement | null) => {
    if (container) {
      container.innerHTML =
        '<div style="display:flex; align-items:center; justify-content:center; width:100%; height:100%; background-color:#f5f5f5; color:#888; font-size:14px;">지도를 불러올 수 없습니다.</div>';
    }
  };

  useEffect(() => {
    const mapContainer = document.getElementById("map"); // 지도를 표시할 div

    if (!(window as any).kakao?.maps?.LatLng) {
      console.error("카카오맵 SDK가 로드되지 않았습니다. 카카오 개발자 콘솔의 등록 도메인과 네트워크 상태를 확인하세요.");
      showMapUnavailable(mapContainer);
      return;
    }

    try {
    const initialPosition = new (window as any).kakao.maps.LatLng(
      props.initLocation.latitude,
      props.initLocation.hardness
    ); // 북한산
    const mapOptions = {
      center: initialPosition,
      level: props.currentLevel, // 초기 확대 레벨
      maxLevel: props.maxLevel, // 최대 확대 레벨 설정
    };

    // 지도 생성
    const map = new (window as any).kakao.maps.Map(mapContainer, mapOptions);
    map.addOverlayMapTypeId((window as any).kakao.maps.MapTypeId.TERRAIN);

    if (props.initMaxLocation) {
      const bounds = new (window as any).kakao.maps.LatLngBounds(
        new (window as any).kakao.maps.LatLng(
          props.initMaxLocation.leftLatitude,
          props.initMaxLocation.leftHardness
        ), // 좌하
        new (window as any).kakao.maps.LatLng(
          props.initMaxLocation.rightLatitude,
          props.initMaxLocation.rightHardness
        ) // 우상
      );

      new (window as any).kakao.maps.event.addListener(map, "drag", () => {
        const center = map.getCenter();

        // 현재 중심 좌표가 bounds 안에 있는지 확인
        if (!bounds.contain(center)) {
          map.setCenter(initialPosition);
        }
      });

      new (window as any).kakao.maps.event.addListener(
        map,
        "zoom_changed",
        () => {
          const center = map.getCenter();

          // 줌 레벨에 따라 범위 확인
          if (!bounds.contain(center)) {
            // 중심이 bounds를 벗어나면 원래 중심으로 되돌리기
            map.setCenter(initialPosition);
          }
        }
      );
      // 초기 중심 좌표를 고정된 영역의 중심으로 설정
      map.setCenter(initialPosition);
    }

    props.markerList?.forEach((item) => {
      const markerPosition = new (window as any).kakao.maps.LatLng(
        item.latitude,
        item.hardness
      );
      // 마커 색상: 최근 수집 회차 미수신 → 회색 / 임계치 초과 항목 있음 → 빨강 / 정상 수신 → 검정
      let markerSrc;
      if (item.dataReceived === false) {
        markerSrc = grayImage;
      } else if (item.markType.length > 0) {
        markerSrc = redImage;
      } else {
        markerSrc = blackImage;
      }
      const markerImage = new (window as any).kakao.maps.MarkerImage(
        markerSrc,
        new (window as any).kakao.maps.Size(10, 10)
      );

      const marker = new (window as any).kakao.maps.Marker({
        position: markerPosition,
        image: markerImage,
      });

      marker.setMap(map);

      if (item.isFocus === true) {
        const customOverlay = new (window as any).kakao.maps.CustomOverlay({
          position: markerPosition,
          content: '<div class="blinking-marker"></div>',
          yAnchor: 0, // 위치 조정
        });

        // CustomOverlay를 지도에 추가
        customOverlay.setMap(map);
      } else {
        const customOverlay = new (window as any).kakao.maps.CustomOverlay({
          position: markerPosition,
          yAnchor: 1, // 위치 조정
        });

        // CustomOverlay를 지도에 추가
        customOverlay.setMap(map);
      }

      // const markerElement = marker.getElement();
      // if (markerElement) {
      //     markerElement.classList.add('your-class-name');
      // }

      if (item.tooltipTextList) {
        let tooltipText = "";
        item.tooltipTextList.map((tooltip) => tooltipText += `<span style="color:${item.markType.includes(tooltip.type) ? 'orangered;':'white;'}">${tooltip.tooltipText}</span><br/>`);
        const tooltipContent = `<div
            style="
                            background-color: black; 
                            position: absolute; 
                            z-index: 99999; 
                            border: 1px solid #333; 
                            padding: 8px; 
                            font-family: Arial, sans-serif; 
                            font-size: 14px;
                            border-radius: 4px; 
                            color:${item.markType.includes('temp')?'orangered':'white'};
                            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.5); 
        >${tooltipText}</div>`;
        
        const tooltip = new (window as any).kakao.maps.CustomOverlay({
          content: tooltipContent,
          position: markerPosition,
          xAnchor: -0.1,
          yAnchor: 0.1,
        });

        new (window as any).kakao.maps.event.addListener(
          marker,
          "mouseover",
          () => {
            tooltip.setMap(map); // 클릭 시 툴팁 표시
          }
        );

        new (window as any).kakao.maps.event.addListener(
          marker,
          "mouseout",
          () => {
            tooltip.setMap(null); // 클릭 시 툴팁 숨김
          }
        );
      }
      new (window as any).kakao.maps.event.addListener(marker, "click", () => {
        // tooltip.setMap(map); // 클릭 시 툴팁 표시
        navigate("/dashboard-detail", {
          state: { ntnlPntNo: item.ntnlPntNo, sggValue: item.sggValue },
        });
      });
    });

    // 하이라이트할 영역 좌표 설정
    let positions: any[] = [];
    if (props.isInitLocationFlag) {
      BukMountainlocation.forEach((item) => {
        positions.push(
          new (window as any).kakao.maps.LatLng(item.latitude, item.hardness)
        );
      });
    } else {
      positions = props.mapPath ? props.mapPath : [];
    }

    // map.setDraggable(props.isDragFlag);

    if (positions.length > 0) {
      // 다각형 생성
      const polygon = new (window as any).kakao.maps.Polygon({
        path: positions,
        strokeWeight: 2, // 테두리 두께
        strokeColor: "#d9fb69", // 테두리 색
        strokeOpacity: 0.8, // 테두리 투명도
        fillColor: "#04d623", // 내부 색
        fillOpacity: 0.4, // 내부 투명도
      });

      // 다각형을 지도에 표시
      polygon.setMap(map);
    }

    const overlay = document.createElement("div");
    overlay.style.position = "absolute";
    overlay.style.width = "100%";
    overlay.style.height = "100%";
    overlay.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
    overlay.style.pointerEvents = "none";

    mapContainer?.appendChild(overlay);
    } catch (e) {
      //지도 초기화 중 오류가 나도 앱 전체가 내려가지 않도록 지도 영역만 비운다
      console.error("카카오맵 초기화 중 오류가 발생했습니다.", e);
      showMapUnavailable(mapContainer);
    }
  }, []);

  return (
    <div
      id="map"
      ref={mapContainerRef}
      style={{ width: props.mapWidth, height: props.mapHeight }}
    ></div>
  );
};

export default KakaoMap;
